import React, { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { TextField, Tooltip, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Handle, Position } from "reactflow";
import { HandleDot } from "../handle";
import { useTheme } from "../../../contexts/ThemeContext";
import { getData } from "../../../apis/api";
import { ENDPOINTS } from "../../../apis/urls";

export const HttpRequestEditModal = ({ open, handleClose, data }) => {
  const { darkMode } = useTheme();
  const isEdit = !!data?.id;

  const currentConfig = data?.data?.config || {};
  const [method, setMethod] = useState(currentConfig.method || "POST");
  const [url, setUrl] = useState(currentConfig.url || "");
  const [authSecretId, setAuthSecretId] = useState(currentConfig.authSecretId || "");
  const [headersText, setHeadersText] = useState(
    currentConfig.headers ? JSON.stringify(currentConfig.headers, null, 2) : ""
  );
  const [bodyText, setBodyText] = useState(
    typeof currentConfig.body === "object"
      ? JSON.stringify(currentConfig.body, null, 2)
      : currentConfig.body || ""
  );
  const [timeoutSeconds, setTimeoutSeconds] = useState(currentConfig.timeoutSeconds ?? 15);
  const [maxAttempts, setMaxAttempts] = useState(currentConfig.maxAttempts ?? 3);
  const [captureText, setCaptureText] = useState(
    currentConfig.capture ? JSON.stringify(currentConfig.capture, null, 2) : ""
  );

  const [secrets, setSecrets] = useState([]);

  useEffect(() => {
    if (!open) return;
    getData(ENDPOINTS.WORKFLOW.SECRETS)
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.Content || [];
        setSecrets(list);
      })
      .catch(() => setSecrets([]));
  }, [open]);

  useEffect(() => {
    if (data?.data?.config) {
      const cfg = data.data.config;
      setMethod(cfg.method || "POST");
      setUrl(cfg.url || "");
      setAuthSecretId(cfg.authSecretId || "");
      setHeadersText(cfg.headers ? JSON.stringify(cfg.headers, null, 2) : "");
      setBodyText(
        typeof cfg.body === "object" ? JSON.stringify(cfg.body, null, 2) : cfg.body || ""
      );
      setTimeoutSeconds(cfg.timeoutSeconds ?? 15);
      setMaxAttempts(cfg.maxAttempts ?? 3);
      setCaptureText(cfg.capture ? JSON.stringify(cfg.capture, null, 2) : "");
    }
  }, [data]);

  const onSave = () => {
    let parsedHeaders = {};
    if (headersText.trim()) {
      try {
        parsedHeaders = JSON.parse(headersText);
      } catch (err) {
        alert("Headers must be valid JSON");
        return;
      }
    }

    let parsedBody = bodyText;
    if (bodyText.trim() && (bodyText.trim().startsWith("{") || bodyText.trim().startsWith("["))) {
      try {
        parsedBody = JSON.parse(bodyText);
      } catch (e) {}
    }

    let parsedCapture = {};
    if (captureText.trim()) {
      try {
        parsedCapture = JSON.parse(captureText);
      } catch (err) {
        alert("Capture mapping must be valid JSON");
        return;
      }
    }

    const config = {
      method: method.toUpperCase(),
      url: url.trim(),
      authSecretId: authSecretId ? Number(authSecretId) : undefined,
      headers: parsedHeaders,
      body: parsedBody,
      timeoutSeconds: Number(timeoutSeconds) || 15,
      maxAttempts: Number(maxAttempts) || 3,
      capture: parsedCapture,
    };

    handleClose({ config, urlSnippet: `${method} ${url}` }, isEdit);
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 550,
    maxWidth: "95vw",
    maxHeight: "90vh",
    overflowY: "auto",
    bgcolor: darkMode ? "#18181b" : "#ffffff",
    border: `1px solid ${darkMode ? "#27272a" : "#e2e8f0"}`,
    borderRadius: 3,
    boxShadow: 24,
    p: 3.5,
  };

  return (
    <Modal open={open} onClose={() => handleClose(null)}>
      <Box sx={modalStyle}>
        <div className="flex items-center gap-2 mb-2">
          <div className="p-2 rounded-lg bg-purple-500/10 text-purple-500">
            <LanguageIcon />
          </div>
          <div>
            <h2 className={`text-lg font-bold ${darkMode ? "text-white" : "text-slate-900"}`}>
              {isEdit ? "Edit HTTP Request Node" : "Configure HTTP Request"}
            </h2>
            <p className={`text-xs ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>
              Make external API calls, post webhooks, and capture response payloads into workflow context.
            </p>
          </div>
        </div>

        <div className="space-y-4 my-4">
          <div className="flex gap-2">
            <FormControl size="small" style={{ width: 130 }}>
              <InputLabel id="http-method-label">Method</InputLabel>
              <Select
                labelId="http-method-label"
                value={method}
                label="Method"
                onChange={(e) => setMethod(e.target.value)}
              >
                {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              size="small"
              label="URL Endpoint *"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://api.crm.com/v1/leads/update"
            />
          </div>

          <FormControl fullWidth size="small">
            <InputLabel id="http-secret-label">Authentication Secret (Optional)</InputLabel>
            <Select
              labelId="http-secret-label"
              value={authSecretId}
              label="Authentication Secret (Optional)"
              onChange={(e) => setAuthSecretId(e.target.value)}
            >
              <MenuItem value="">None / Custom Headers</MenuItem>
              {secrets.map((sec) => (
                <MenuItem key={sec.id} value={sec.id}>
                  {sec.name} ({sec.description || "Stored Secret"})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <div>
            <label className={`text-xs font-semibold block mb-1 ${darkMode ? "text-zinc-300" : "text-slate-700"}`}>
              Request Headers (JSON object)
            </label>
            <TextField
              fullWidth
              multiline
              rows={2}
              size="small"
              value={headersText}
              onChange={(e) => setHeadersText(e.target.value)}
              placeholder='{"Authorization": "Bearer token", "Content-Type": "application/json"}'
              inputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
            />
          </div>

          <div>
            <label className={`text-xs font-semibold block mb-1 ${darkMode ? "text-zinc-300" : "text-slate-700"}`}>
              Request Body (JSON or text template with &#123;&#123;tags&#125;&#125;)
            </label>
            <TextField
              fullWidth
              multiline
              rows={3}
              size="small"
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              placeholder='{"phone": "{{contact.phone}}", "status": "contacted"}'
              inputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <TextField
              size="small"
              type="number"
              label="Timeout (1-30s)"
              value={timeoutSeconds}
              onChange={(e) => setTimeoutSeconds(Math.max(1, Math.min(30, Number(e.target.value))))}
              inputProps={{ min: 1, max: 30 }}
            />
            <TextField
              size="small"
              type="number"
              label="Max Attempts (1-5)"
              value={maxAttempts}
              onChange={(e) => setMaxAttempts(Math.max(1, Math.min(5, Number(e.target.value))))}
              inputProps={{ min: 1, max: 5 }}
            />
          </div>

          <div>
            <label className={`text-xs font-semibold block mb-1 ${darkMode ? "text-zinc-300" : "text-slate-700"}`}>
              Capture Response into Context (JSON mapping context_key : json_path)
            </label>
            <TextField
              fullWidth
              multiline
              rows={2}
              size="small"
              value={captureText}
              onChange={(e) => setCaptureText(e.target.value)}
              placeholder='{"crm_id": "data.id", "status": "response.status"}'
              inputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 dark:border-zinc-700">
          <Button variant="outlined" onClick={() => handleClose(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={!url.trim()}
            style={{ backgroundColor: "#8b5cf6" }}
          >
            {isEdit ? "Update Node" : "Add Node"}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export const HttpRequestNode = (props) => {
  const { data, selected, onEdit, onAdd, onDeleteNode, edges = [], ...rest } = props;
  const { darkMode } = useTheme();

  const cfg = data?.config || {};
  const method = (cfg.method || "POST").toUpperCase();
  const urlDisplay = cfg.url || "https://...";

  const branches = [
    { key: "success", label: "2xx Success", color: "bg-emerald-500" },
    { key: "error", label: "Error / Timeout", color: "bg-rose-500" },
    { key: "next", label: "Next", color: "bg-slate-400" },
  ];

  const isBranchConnected = (branchKey) =>
    edges.some((e) => e.source === rest.id && (e.sourceHandle === branchKey || (!e.sourceHandle && branchKey === "next")));

  return (
    <div
      className={`w-60 shadow-lg rounded-xl overflow-hidden transition-all duration-200 ${
        darkMode ? "bg-[#18181b] border border-zinc-700" : "bg-white border border-slate-200"
      } ${
        selected ? "ring-2 ring-purple-500 shadow-purple-500/20" : "hover:border-purple-400"
      }`}
    >
      <Handle id="target" type="target" position={Position.Left} />

      <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="flex items-center gap-1.5 min-w-0">
          <LanguageIcon style={{ fontSize: 16 }} />
          <span className="text-xs font-semibold tracking-wide uppercase truncate">
            HTTP Webhook
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Tooltip title="Edit Node">
            <EditIcon
              style={{ fontSize: 14, cursor: "pointer" }}
              onClick={() => onEdit(props)}
            />
          </Tooltip>
          {onDeleteNode && rest.id !== "1" && (
            <Tooltip title="Delete Node">
              <DeleteOutlineIcon
                style={{ fontSize: 15, cursor: "pointer" }}
                onClick={() => onDeleteNode(rest.id)}
              />
            </Tooltip>
          )}
        </div>
      </div>

      <div className="p-2.5">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-purple-500/20 text-purple-600 dark:text-purple-300">
            {method}
          </span>
          <span className={`text-xs font-mono truncate ${darkMode ? "text-zinc-300" : "text-slate-700"}`}>
            {urlDisplay}
          </span>
        </div>
        <div className={`text-[10px] ${darkMode ? "text-zinc-500" : "text-slate-400"}`}>
          Timeout: {cfg.timeoutSeconds || 15}s | Max: {cfg.maxAttempts || 3}x
        </div>
      </div>

      <div className={`border-t ${darkMode ? "border-zinc-800 bg-zinc-900/50" : "border-slate-100 bg-slate-50/50"} px-2.5 py-2 space-y-1.5`}>
        <div className="text-[10px] font-medium uppercase tracking-wider text-slate-400 mb-1">
          Response Status
        </div>
        {branches.map((b) => {
          const connected = isBranchConnected(b.key);
          return (
            <div key={b.key} className="flex items-center justify-between relative py-0.5">
              <div className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${b.color}`} />
                <span className={`text-[11px] font-medium ${darkMode ? "text-zinc-300" : "text-slate-600"}`}>
                  {b.label}
                </span>
              </div>
              <HandleDot
                addVisible={!connected}
                id={b.key}
                type="source"
                position={Position.Right}
                onAdd={(v) =>
                  onAdd(v, rest, rest.id, {
                    type: "HTTP_REQUEST",
                    key: b.key,
                  })
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

